import { recordLiveChange } from './liveSync';

/**
 * Live changes: statement-level change broadcasts from the database
 * (supabase/core-crm/live_changes/). One message per INSERT/UPDATE/DELETE statement,
 * carrying the ids it touched, on the private `live-changes` topic.
 *
 * Messages are coalesced per table and operation for a short window, then:
 *  - React Query caches reading the table are invalidated (liveSync), and
 *  - a `${table}_${op}` window event is dispatched for the surgical consumers
 *    (grids, record detail form, record history) with the merged ids, and
 *  - one LIVE_CHANGES_FLUSH_EVENT carrying every change of the window, for consumers that
 *    follow writes across all tables (the audit log).
 *
 * A bulk write (lead import autofill, mass edits) therefore reaches each tab as a few
 * events with id lists instead of one event per row.
 */

export const LIVE_CHANGES_TOPIC = 'live-changes';
export const LIVE_CHANGES_EVENT = 'change';
/** Window event dispatched once per coalescing window; detail is LiveChangeEventDetail[]. */
export const LIVE_CHANGES_FLUSH_EVENT = 'live_changes_flush';

/** Matches core_crm.live_change_id_limit(); beyond it consumers refresh everything. */
const MAX_IDS = 2000;
const COALESCE_MS = 250;

export type LiveChangeOperation = 'INSERT' | 'UPDATE' | 'DELETE';

export interface LiveChangeMessage {
  schema: string;
  table: string;
  op: LiveChangeOperation;
  /** null: too many rows changed to list, treat every row of the table as changed. */
  ids: number[] | null;
}

/** detail of the `${table}_${op}` window events. */
export interface LiveChangeEventDetail {
  schema: string;
  table: string;
  eventType: LiveChangeOperation;
  ids: number[] | null;
}

interface PendingChange {
  schema: string;
  table: string;
  op: LiveChangeOperation;
  ids: Set<number> | null;
}

const pending = new Map<string, PendingChange>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const isOperation = (value: unknown): value is LiveChangeOperation =>
  value === 'INSERT' || value === 'UPDATE' || value === 'DELETE';

export const parseLiveChangeMessage = (payload: unknown): LiveChangeMessage | null => {
  if (!payload || typeof payload !== 'object') return null;
  const { schema, table, op, ids } = payload as Record<string, unknown>;
  if (typeof schema !== 'string' || typeof table !== 'string' || !isOperation(op)) return null;
  if (ids === null) return { schema, table, op, ids: null };
  if (!Array.isArray(ids)) return null;
  return { schema, table, op, ids: ids.map(Number).filter(Number.isFinite) };
};

/** Does a change event touch this record? */
export const liveChangeIncludesId = (detail: LiveChangeEventDetail | undefined, id: unknown): boolean => {
  if (!detail || id === undefined || id === null) return false;
  if (detail.ids === null) return true;
  const target = Number(id);
  return detail.ids.some((changedId) => changedId === target);
};

const flush = (): void => {
  flushTimer = null;
  const changes = Array.from(pending.values());
  pending.clear();

  const details = changes.map(({ schema, table, op, ids }): LiveChangeEventDetail => {
    recordLiveChange({ schema, table, eventType: op });
    const detail: LiveChangeEventDetail = {
      schema,
      table,
      eventType: op,
      ids: ids === null ? null : Array.from(ids)
    };
    globalThis.dispatchEvent(new CustomEvent(`${table}_${op.toLowerCase()}`, { detail }));
    return detail;
  });
  globalThis.dispatchEvent(new CustomEvent(LIVE_CHANGES_FLUSH_EVENT, { detail: details }));
};

export const receiveLiveChange = (message: LiveChangeMessage): void => {
  const key = `${message.schema}.${message.table}:${message.op}`;
  const current = pending.get(key) ?? { schema: message.schema, table: message.table, op: message.op, ids: new Set<number>() };

  if (message.ids === null || current.ids === null) {
    current.ids = null;
  } else {
    message.ids.forEach((id) => current.ids?.add(id));
    if (current.ids.size > MAX_IDS) current.ids = null;
  }
  pending.set(key, current);

  if (flushTimer === null) flushTimer = setTimeout(flush, COALESCE_MS);
};
